import { createBrowserRouter, type RouteObject } from 'react-router';

import { GuestOnly, RequireAuth } from '@/shared/auth/RequireAuth';
import { SessionSplash } from '@/shared/auth/SessionSplash';
import type { ModuleRegistry } from '@/shared/registry/registry';

import { RestrictedGate } from './auth/RestrictedGate';
import { NotFoundPage } from './errors/NotFoundPage';
import { RootErrorPage, RouteErrorPage } from './errors/RouteErrorPage';
import { AppShell } from './layout/AppShell';
import { RootLayout } from './RootLayout';

/** Developer-only routes (tree-shaken from production builds). */
const devRoutes: RouteObject[] = import.meta.env.DEV
  ? [{ path: 'dev/ui', lazy: async () => ({ Component: (await import('./dev/UiShowcasePage')).default }) }]
  : [];

/** Route tree: public auth pages, the modules' public pages + the authenticated shell with every module's routes. */
export function createAppRoutes(registry: ModuleRegistry): RouteObject[] {
  return [
    {
      Component: RootLayout,
      ErrorBoundary: RootErrorPage,
      HydrateFallback: SessionSplash,
      children: [
        // Auth pages load on demand: their form stack (react-hook-form, zod) is not needed once signed in.
        {
          path: 'login',
          lazy: async () => {
            const { LoginPage } = await import('./auth/LoginPage');
            return {
              Component: () => (
                <GuestOnly>
                  <LoginPage />
                </GuestOnly>
              ),
            };
          },
        },
        {
          path: 'forgot-password',
          lazy: async () => {
            const { ForgotPasswordPage } = await import('./auth/ForgotPasswordPage');
            return {
              Component: () => (
                <GuestOnly>
                  <ForgotPasswordPage />
                </GuestOnly>
              ),
            };
          },
        },
        // The reset link opens whoever follows it (also somebody still signed in here): the page signs the old session out.
        {
          path: 'reset-password',
          lazy: async () => ({ Component: (await import('./auth/ResetPasswordPage')).ResetPasswordPage }),
        },
        {
          path: 'invite/:token',
          lazy: async () => ({ Component: (await import('./auth/InvitePage')).InvitePage }),
        },
        // Pages modules publish without sign-in (e.g. the disk's public links).
        ...registry.modules.flatMap((m) => m.publicRoutes ?? []),
        {
          element: (
            <RequireAuth>
              <RestrictedGate>
                <AppShell />
              </RestrictedGate>
            </RequireAuth>
          ),
          children: [
            {
              // Errors inside a page render within the shell, keeping navigation available.
              ErrorBoundary: RouteErrorPage,
              children: [
                ...registry.modules.flatMap((m) => m.routes),
                ...devRoutes,
                { path: '*', Component: NotFoundPage },
              ],
            },
          ],
        },
      ],
    },
  ];
}

export const createAppRouter = (registry: ModuleRegistry) => createBrowserRouter(createAppRoutes(registry));
